import { SiAdobe } from "react-icons/si";
import HostedVerticalMedia from "../components/HostedVerticalMedia";
import ProfileImage from "../images/nathancolesjr.png";

const experienceCards = [
  {
    title: "Content strategy",
    body: "Planned short-form around hooks, packaging, pacing, and repeatable formats that fit how people actually watch.",
  },
  {
    title: "Production and editing",
    body: "Shot, edited, packaged, and posted content myself across my own pages and client-facing work.",
  },
  {
    title: "Growth and response",
    body: "Used volume, iteration, and clearer positioning to turn content into reach, followers, inquiries, and sales.",
  },
];

const portfolioStats = [
  {
    value: "500K-2.5M",
    supporting: "Views every 30 days",
  },
  {
    value: "0-20,000",
    supporting: "Followers in 2 months",
    note: "(currently 40,000)",
    featured: true,
  },
  {
    value: "1,000+",
    supporting: "Short-form pieces posted",
  },
];

const caseStudies = [
  {
    title: "Hook-led promo content",
    results: "903K views • 111K likes • 4,000+ followers gained",
    why: "A strong first-second hook, immediate transformation, and clear visual payoff made the content easy to understand and hard to scroll past.",
    hostedVideoUrl: "/media/business/director_edit_ex.mp4",
    posterUrl: "",
    instagramUrl: "https://www.instagram.com/p/DKz3-AMRIAV/",
  },
  {
    title: "Educational short-form content",
    meta: {
      label: "Collaboration with Adobe",
      logo: "adobe",
    },
    results: "45.4K likes • 512,231 views • 3.4K followers gained",
    why: "Clear, useful, opinion-driven content built trust quickly and gave people a reason to engage with a specific topic.",
    hostedVideoUrl: "/media/business/aftereffects_vs_premiere_ex.mp4",
    posterUrl: "",
    instagramUrl: "https://www.instagram.com/p/DLz9UPfsxNo/",
  },
  {
    title: "Talking-head short-form content",
    results: "329,292 views • 251,676 accounts reached • 29,209 likes • 12,783 shares",
    why: "A clear delivery style, a strong angle, and helpful content people wanted to pass along helped drive both reach and engagement at a high level.",
    hostedVideoUrl: "/media/business/nathancolesjr_portfolio_talkinghead.mp4",
    posterUrl: "",
    instagramUrl: "",
  },
  {
    title: "Offer-driven short-form content",
    results: "14K views • 367 referrals • $614 in attributed sales",
    why: "A clear value proposition, relevant audience fit, and direct offer integration helped turn a relatively modest-view reel into measurable conversions.",
    hostedVideoUrl: "/media/business/plugin_affiliate_ex.mp4",
    posterUrl: "",
    instagramUrl: "https://www.instagram.com/p/DTG37RzAb9i/",
    compactMedia: true,
  },
];

const broaderResults = [
  {
    title: "4,000+ followers gained from a single post",
    supporting: "A strong hook and clear payoff helped turn one post into meaningful audience growth.",
  },
  {
    title: "200+ paid video inquiries generated from a single short-form video",
    supporting: "One short-form post created direct response by getting over 200 people to opt in through a form for a paid video.",
  },
  {
    title: "Content contributed to a major increase in inbound business and monthly revenue",
    supporting: "As I improved hooks, packaging, and consistency, content began driving stronger business results, not just reach.",
  },
  {
    title: "A story-led music video and month-long short-form campaign helped one artist roughly double his following",
    supporting: "This showed how consistent creative direction and supporting content can help grow attention around a release.",
  },
];

function PortfolioPage() {
  return (
    <main className="pageMain">
      <section className="section businessHero">
        <div className="businessHero__layout">
          <div className="businessHero__content">
            <h1>Nathan Coles Jr.</h1>
            <p>
              Short-form content, editing, packaging, and content strategy backed by real reach, growth, and
              measurable response.
            </p>
            <div className="businessHero__actions">
              <a className="button button--secondary" href="#case-studies">
                View Case Studies
              </a>
              <a className="button button--primary" href="mailto:nathancolesjr@gmail.com">
                Email Me
              </a>
            </div>
          </div>
          <div className="businessHero__portraitWrap" aria-hidden="true">
            <div className="businessHero__portraitAccent" />
            <img className="businessHero__portrait" src={ProfileImage} alt="" />
          </div>
        </div>
      </section>

      <section className="section businessSection businessSection--services">
        <div className="sectionHeading">
          <h2>What I&apos;ve done</h2>
        </div>

        <div className="businessCardGrid">
          {experienceCards.map((card) => (
            <article key={card.title} className="businessCard">
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section businessSection businessSection--proof">
        <div className="sectionHeading">
          <h2>Stats</h2>
        </div>

        <div className="businessStatsGrid">
          {portfolioStats.map((stat) => (
            <article
              key={stat.supporting}
              className={`businessStatCard${stat.featured ? " businessStatCard--featured" : ""}`}
            >
              <span className="businessStatCard__value">{stat.value}</span>
              <span className="businessStatCard__supporting">{stat.supporting}</span>
              {stat.note ? <span className="businessStatCard__note">{stat.note}</span> : null}
            </article>
          ))}
        </div>

        <div className="businessSection__body businessSection__body--split">
          <p>These results come from content I planned, shot, edited, packaged, and posted on my own pages.</p>
          <p>
            The work covers ideation, production, editing, and the content decisions that affect watch time,
            response, and overall performance.
          </p>
        </div>
      </section>

      <section className="section businessSection businessSection--work" id="case-studies">
        <div className="sectionHeading">
          <h2>Case studies</h2>
          <p>Examples of how I structure content, package ideas, and turn attention into measurable results.</p>
        </div>

        <div className="businessWorkGrid businessWorkGrid--proof">
          {caseStudies.map((item) => (
            <PortfolioCaseStudy key={item.title} item={item} />
          ))}
          <PortfolioResultsPanel />
        </div>
      </section>

      <section className="section businessCta">
        <div className="businessCta__card">
          <h2>Want the full context?</h2>
          <p>I can send more work, analytics, or talk through the role directly.</p>
          <div className="businessCta__actions">
            <a
              className="button button--secondary"
              href="https://instagram.com/nathancolesjr"
              target="_blank"
              rel="noreferrer"
            >
              Instagram
            </a>
            <a className="button button--primary" href="mailto:nathancolesjr@gmail.com">
              Email Me
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

function PortfolioCaseStudy({ item }) {
  return (
    <article className="businessWorkProofCard">
      <h3>{item.title}</h3>
      {item.meta ? (
        <p className="businessWorkProofCard__meta">
          {item.meta.logo === "adobe" ? <SiAdobe aria-hidden="true" /> : null}
          <span>{item.meta.label}</span>
        </p>
      ) : null}
      <p className="businessWorkProofCard__results">{item.results}</p>
      <p className="businessWorkProofCard__why">{item.why}</p>
      <HostedVerticalMedia
        mediaUrl={item.hostedVideoUrl}
        posterUrl={item.posterUrl}
        instagramUrl={item.instagramUrl}
        title={item.title}
        compact={item.compactMedia}
      />
    </article>
  );
}

function PortfolioResultsPanel() {
  return (
    <article className="businessImpactCard">
      <h3>Broader results</h3>
      <p className="businessImpactCard__intro">
        Not everything shows up in one post. These are broader outcomes from how I planned, posted, and
        refined content over time.
      </p>
      <div className="businessImpactCard__grid">
        {broaderResults.map((item) => (
          <div key={item.title} className="businessImpactCard__item">
            <strong>{item.title}</strong>
            <p>{item.supporting}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

export default PortfolioPage;
